import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucideEllipsisVertical, lucidePen, lucidePlus, lucideTrash } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { toast } from 'ngx-sonner';
import { filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../cartographie/models/cartographie.model';
import { ConfirmDialogComponent } from '../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { SidepanelService } from '../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../shared/pipes/trier-par.pipe';
import { FormModalitéComponent } from './form-modalité/form-modalité.component';
import { FormModalitésComponent } from './form-modalités/form-modalités.component';
import { Modalité } from './models/modalité.model';
import { ModalitéStore } from './store/modalité.store';
import { PaginateurComponent } from '@shared/components/paginateur/paginateur.component';

@Component({
  selector: 'app-modalites',
  imports: [
    PaginateurComponent,
    HlmCardImports,
    HlmDropdownMenuImports,
    HlmButtonImports,
    HlmTableImports,
    HlmIconImports,
    TrierParPipe,
  ],
  templateUrl: './modalités.component.html',
  providers: [provideIcons({ lucideEllipsisVertical, lucidePlus, lucidePen, lucideTrash })],
})
export class ModalitésComponent {
  readonly store = inject(ModalitéStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);

  readonly cartographie = input.required<Cartographie>();

  readonly modalites = computed(() => this.store.entities());

  constructor() {
    effect(() => this.store.charger(this.cartographie().id));
  }

  ajouterModalite() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une modalité',
      FormModalitésComponent,
      `Vous pouvez créer une nouvelle modalité seulement, ou en créer plusieurs d’un seul coup.`,
      { idCartographie: this.cartographie().id },
    );
    this.#sidepanelService.open();
  }

  modifierModalite(modalité: Modalité) {
    if (!modalité) return;

    this.#sidepanelService.setComponentToDisplay(
      'Modifier une modalité',
      FormModalitéComponent,
      `Vous pouvez modifier la modalité sélectionnée.`,
      { idCartographie: this.cartographie().id, modalite: modalité },
    );
    this.#sidepanelService.open();
  }

  supprimerModalite(modalité: Modalité) {
    if (!modalité) return;

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer la modalité`,
        contenu: `Êtes-vous sûr de vouloir supprimer la modalité <b>${modalité.nom}</b> ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });
    const toastrId = uuidv7();

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result) => result),
      )
      .subscribe(() => {
        this.store.supprimer({
          id: modalité.id,
          next: () =>
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: 'Suppression de la modalité réussie', id: toastrId },
              id: toastrId,
            }),
        });
      });
  }
}
